import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Header from './components/Header';
import SEO from './components/SEO';
import Main from './pages/Main';
import Category from './pages/Category';
import Details from './pages/Details';
import SearchPage from './pages/SearchPage';
import ScreenSizeInfoProvider from './ScreenSizeInfoProvider';
import ErrorBoundary from '../../lib/ErrorBoundary';
import QuickLinks from './components/QuickLinks';

const AppCenter = props => {
  const {
    fptiImpression,
    location: { pathname },
    structuredPSData,
    productIntentTools,
    fptiClick,
    flatPSData = {},
    dispatch,
    toggleToolFavorite,
    tools,
    clearSearch,
    app,
    toolServFavorites,
    appSearch,
    staticContext,
    experimentation,
    quicklinks
  } = props;

  const { categories = [] } = structuredPSData;
  const { coloredIconsExp: isColoredIconsEligible } = experimentation;

  // the CategoryToolTile is nested several layers deep in a few places
  // group all these props together to make it easier to pass them down
  // as they're only used by CategoryToolTile component
  const toolTileProps = {
    flatPSData,
    dispatch,
    toggleToolFavorite,
    tools,
    app,
    toolServFavorites,
    productIntentTools,
    experimentation
  };

  // When route changes, scroll to top and take impression of page.
  useEffect(() => {
    window.scrollTo(0, 0);
    fptiImpression({ amplitude: true });
  }, [pathname]);

  const [, , categorySlug, detailsSlug] = pathname.split('/');

  const getPage = (categorySlug, detailsSlug) => {
    if (props.appSearch.query) {
      return (
        <SearchPage
          toolTileProps={toolTileProps}
          fptiClick={fptiClick}
          appSearch={appSearch}
          deviceInfo={app?.deviceInfo}
          fptiImpression={props.fptiImpression}
          dispatch={dispatch}
          clearSearch={clearSearch}
          quicklinks={quicklinks}
          experimentation={experimentation}
        />
      );
    }

    if (detailsSlug) {
      return <Details {...props} toolTileProps={toolTileProps} />;
    } else if (categorySlug) {
      const category = categories.find(
        ({ content = {} }) => content.location === categorySlug
      );
      const { staticContext = {}, app } = props;
      return (
        <Category
          category={category}
          fptiClick={fptiClick}
          fptiImpression={fptiImpression}
          toolTileProps={toolTileProps}
          staticContext={staticContext}
          app={app}
          isColoredIconsEligible={isColoredIconsEligible}
        />
      );
    }
    return (
      <Main
        structuredPSData={structuredPSData}
        fptiClick={fptiClick}
        fptiImpression={fptiImpression}
        toolTileProps={toolTileProps}
        defaultRecommended={flatPSData.defaultRecommended}
        experimentation={experimentation}
        quicklinks={quicklinks}
      />
    );
  };
  const {
    structuredPSData: { content: { imageUrl = '' } = {} } = {},
    location = {},
    seo = {},
    app: {
      deviceInfo: { isDesktop } = {},
      context: { user: { isLoggedIn } = {} } = {}
    } = {},
    experimentation: { moduleFederation }
  } = props;
  let displayName = 'main';
  if (categorySlug) displayName = 'category';
  if (detailsSlug) displayName = 'details';

  return (
    <ErrorBoundary fptiImpression={fptiImpression}>
      <ScreenSizeInfoProvider staticContext={staticContext}>
        <div id="merchanthub-appcenter" className={displayName}>
          <SEO
            mainImageUrl={imageUrl}
            seoData={seo}
            location={location}
            categorySlug={categorySlug}
            detailsSlug={detailsSlug}
            app={app}
          />
          <Header {...props} />
          {/*render in root view on mobile since the footer is sticky to all pages */
          !isDesktop && (
            <QuickLinks
              moduleFederation={moduleFederation}
              isLoggedIn={isLoggedIn}
              isDesktop={isDesktop}
              quicklinks={quicklinks}
            />
          )}
          {getPage(categorySlug, detailsSlug)}
        </div>
      </ScreenSizeInfoProvider>
    </ErrorBoundary>
  );
};

AppCenter.propTypes = {
  toolServFavorites: PropTypes.array,
  app: PropTypes.object,
  dispatch: PropTypes.func,
  location: PropTypes.object,
  fptiClick: PropTypes.func,
  fptiImpression: PropTypes.func,
  toggleToolFavorite: PropTypes.func,
  tools: PropTypes.object,
  structuredPSData: PropTypes.object,
  flatPSData: PropTypes.object,
  productIntentTools: PropTypes.object,
  seo: PropTypes.object,
  staticContext: PropTypes.object,
  appSearch: PropTypes.shape({
    query: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.string)
  }),
  clearSearch: PropTypes.func.isRequired,
  recommendations: PropTypes.array,
  experimentation: PropTypes.object,
  quicklinks: PropTypes.object,
  isColoredIconsEligible: PropTypes.bool
};

export default withRouter(AppCenter);
